// Internationalization: typed catalogs, interpolation, plurals, and locale
// formatting. English is the fallback language; Croatian ships complete.
//
// Interface text is authored as whole messages, never assembled from translated
// fragments. Plurals use `Intl.PluralRules` with an explicit `{one, other, ...}`
// mapping so grammatical number follows the selected locale.
//
// To add a language:
//   1. Add its code to `SUPPORTED_LANGUAGES` in `src/lib/preferences.ts`.
//   2. Copy `src/i18n/catalog-en.ts` to `<code>.ts` and translate every value.
//   3. Import and register the catalog in `MESSAGES` below.
//   4. Confirm `pnpm typecheck` accepts it (the catalog type is exact).

import type { LanguagePreference } from '@/lib/preferences';
import { en } from './catalog-en';
import { hr } from './catalog-hr';

/**
 * Widen a catalog's literal types so translations may hold different text while
 * keeping the exact same key structure. `as const` on the English catalog gives
 * literal values; this mapped type replaces them with `string` and removes
 * readonly, so a missing or extra key is a compile-time error but a different
 * translation is not.
 */
type Widen<T> = T extends string
  ? string
  : T extends number
    ? number
    : T extends boolean
      ? boolean
      : T extends readonly (infer U)[]
        ? Widen<U>[]
        : T extends object
          ? { [K in keyof T]: Widen<T[K]> }
          : T;

/** The shape every catalog must satisfy, derived from English. */
export type Catalog = Widen<typeof en>;

/** Values accepted by `t`: a string, or plural forms keyed by CLDR category. */
export type MessageValue = string | Readonly<Record<string, string>>;

/**
 * CLDR plural categories. An object whose keys are all drawn from this set is a
 * plural message rather than a nested catalog; the check is structural so no
 * marker key is needed in the catalogs.
 */
const PLURAL_CATEGORIES: ReadonlySet<string> = new Set([
  'zero',
  'one',
  'two',
  'few',
  'many',
  'other',
]);

/** Interpolation values substituted into `{name}` placeholders. */
export type InterpolationValues = Record<string, string | number>;

const MESSAGES: Record<LanguagePreference, Catalog> = { en, hr };

/**
 * Flatten nested catalogs into dotted keys so `t('nav.sources')` resolves.
 * Built once per language and cached.
 */
type FlatCatalog = Record<string, MessageValue>;

const flatCache = new Map<LanguagePreference, FlatCatalog>();

function flatten(catalog: unknown, prefix: string, out: FlatCatalog): void {
  if (typeof catalog === 'string') {
    out[prefix] = catalog;
    return;
  }
  if (catalog && typeof catalog === 'object') {
    const entries = Object.entries(catalog as Record<string, unknown>);
    const isPluralForms =
      entries.length > 0 &&
      entries.every(([key, value]) => PLURAL_CATEGORIES.has(key) && typeof value === 'string');
    if (isPluralForms) {
      out[prefix] = catalog as Readonly<Record<string, string>>;
      return;
    }
    for (const [key, value] of entries) {
      flatten(value, prefix ? `${prefix}.${key}` : key, out);
    }
  }
}

function flat(language: LanguagePreference): FlatCatalog {
  const cached = flatCache.get(language);
  if (cached) return cached;
  const out: FlatCatalog = {};
  flatten(MESSAGES[language], '', out);
  flatCache.set(language, out);
  return out;
}

/** Interpolate `{name}` placeholders. Unknown placeholders are left intact. */
export function interpolate(template: string, values?: InterpolationValues): string {
  if (!values) return template;
  return template.replace(/\{(\w+)\}/g, (match, key: string) => {
    const value = values[key];
    return value === undefined ? match : String(value);
  });
}

/** Resolve a plural message for a count in the given language. */
export function pluralize(
  forms: Readonly<Record<string, string>>,
  count: number,
  language: LanguagePreference,
): string {
  const category = new Intl.PluralRules(language).select(count);
  return forms[category] ?? forms.other ?? '';
}

/** Translate a dotted key, falling back to English, then to the key itself. */
export function translate(
  language: LanguagePreference,
  key: string,
  values?: InterpolationValues,
): string {
  const fromSelected = flat(language)[key];
  const value = fromSelected ?? (language === 'en' ? undefined : flat('en')[key]);
  if (value === undefined) {
    if (import.meta.env.DEV) {
      console.warn(`missing translation: ${key} (${language})`);
    }
    return key;
  }
  if (typeof value === 'string') return interpolate(value, values);

  const count = typeof values?.count === 'number' ? values.count : 0;
  return interpolate(pluralize(value, count, language), values);
}
